import { setDateTimeLocalization } from '@racemap/utilities/functions/datetime';
import { ConfigProvider } from 'antd';
import { enableMapSet } from 'immer';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { themeConfig } from 'src/lib/theme';
import { PlayerRouter } from './router/player.router';

import 'normalize.css/normalize.css';

import '@css/player.scss';
import '@css/not-activated.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import screenfull from 'screenfull';

import { parseHash } from 'src/lib/player/controller/HashManager';
import { t } from 'src/lib/player/i18n';
import { loadData } from 'src/lib/player/model';
import { useStore } from 'src/lib/player/store';
import { detectIE, detectWebGL, sleep } from 'src/lib/player/utils';

import WarningSVG from '@assets/player/warning.svg';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { AuthorizationStates } from '@racemap/utilities/consts/events';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { calibrateClock } from 'src/lib/player/utils/clock';

const CDN_HOST = import.meta.env.VITE_CDN_HOST;
const apiClient = new RacemapAPIClient(CDN_HOST, { includeCredentials: false });

setDateTimeLocalization();

// makes the use of Map and Set in immer.js possible
enableMapSet();

/* Window resize handler (for sizing in Android app) */
function handleWindowResize() {
  for (const el of Array.from(document.querySelectorAll('html, body'))) {
    (el as HTMLElement).style.width = `${window.innerWidth}px`;
    (el as HTMLElement).style.height = `${window.innerHeight}px`;
  }
}

window.addEventListener('resize', handleWindowResize, false);
handleWindowResize();

/* Feature detection */
if ('geolocation' in window.navigator) {
  sendAnalyticsEvent('geolocation-supported');
} else {
  sendAnalyticsEvent('geolocation-unsupported');
}

if (screenfull?.isEnabled) {
  sendAnalyticsEvent('fullscreen-supported');
} else {
  sendAnalyticsEvent('fullscreen-unsupported');
}

if (detectWebGL()) {
  sendAnalyticsEvent('webgl-supported');
} else {
  sendAnalyticsEvent('webgl-unsupported');
}

const IEversion = detectIE();
if (IEversion !== false && IEversion < 11) {
  alert(t('Old IE'));
}

async function init() {
  const el = document.querySelector('.racemap');
  if (el == null) return;
  const overlay = el.querySelector('.overlay');
  if (overlay == null) return;
  const loadingIndicator = overlay.querySelector('.loading-indicator');
  if (loadingIndicator == null) return;

  try {
    // search for event id or slug in URL path
    // http(s)://<host>/player/<eventIdOrSlug>
    const location = window.location.pathname;
    // Calibrate clock before loading data to ensure corrected timestamps
    await calibrateClock(location);
    const eventIdOrSlug = location.match(/\/player\/([^/]+)/)?.[1];
    if (eventIdOrSlug == null || isEmptyString(eventIdOrSlug)) {
      throw new Error('No event ID or slug provided in URL');
    }
    console.log('Event ID or Slug from URL:', eventIdOrSlug);

    // Load basic informations of event with slug or id
    const options = await apiClient.getPlayerBaseOptions(eventIdOrSlug);
    const eventId = options.eventId;

    console.log('Config:', `${window.location.origin}/admin/events/${eventId}/visualisation`);

    // If event not authorized, show error
    if (options.authorization === AuthorizationStates.NONE) {
      const div = document.createElement('div');
      div.className = 'not-activated';
      document.body.appendChild(div);
    }

    // Set base color from URL hash parameter or options
    const hashColorMatch = window.location.hash.match(/color=([A-Za-z0-9]{6})/)?.[1];
    if (hashColorMatch != null) {
      document.documentElement.style.setProperty('--base-color', `#${hashColorMatch}`);
    } else if (options.baseColor != null) {
      document.documentElement.style.setProperty('--base-color', options.baseColor);
    } else {
      document.documentElement.style.setProperty('--base-color', RacemapColors.PaleBlue);
    }

    const hashState = parseHash(window.location.hash);
    useStore.getState().actions.receiveOptions(options, hashState);
    const events = await loadData(eventId);

    useStore.getState().actions.receiveBaseData(events, hashState.events ?? [], hashState);

    const container = el.querySelector('.container');
    if (container == null) throw new Error('Could not find root element');

    const root = createRoot(container);
    root.render(
      <StrictMode>
        <ConfigProvider theme={themeConfig}>
          <PlayerRouter />
        </ConfigProvider>
      </StrictMode>,
    );

    overlay.className += ' removed';
    await sleep(300);
    if (overlay.parentElement != null) overlay.parentElement.removeChild(overlay);
    if (import.meta.env.PROD) useStore.getState().actions.resumeTimer();
  } catch (err) {
    loadingIndicator.innerHTML = `<img src="${WarningSVG}" alt="Warning" style="width: 128px; height: 128px;" />`;
    console.error(err);
  }
}
init();
